import * as React from "react";
import { updateMfa } from "../../services/authentication-services";
import { notify } from "../../utilities/toast";
import { config } from "../../config/config";
import { MFASettings } from "../../types/mfa";

const MfaSettings = () => {
  const [mfaSettings, setMfaSettings] = React.useState<MFASettings>({
    mfa_enabled: false,
    mfa_method: []
  });

  const fetchMFASettings = async () => {
    try {
      const response = await fetch(`${config.BACKEND_SERVER_URL}/api/user-profile/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      
      const methods = Array.isArray(data.mfa_method) ? data.mfa_method : [];
      
      setMfaSettings({
        mfa_enabled: data.mfa_enabled || false,
        mfa_method: methods
      });
    } catch (error) {
      console.error('Error fetching MFA settings:', error);
      notify('Error fetching MFA settings', 'error');
    }
  };

  React.useEffect(() => {
    fetchMFASettings();
  }, []);

  const handleToggleChange = async (method: string) => {
    try {
      let newMethods: string[];
      if (mfaSettings.mfa_method.includes(method)) {
        // Remove method if it exists
        newMethods = mfaSettings.mfa_method.filter(m => m !== method);
      } else {
        // Add method if it doesn't exist
        newMethods = [...mfaSettings.mfa_method, method];
      }

      const response = await updateMfa({ mfa_methods: newMethods });
      
      if (response.status === 200) {
        await fetchMFASettings();
        notify('MFA settings updated successfully', 'success');
      } else {
        notify(typeof response.error === 'string' ? response.error : 'Failed to update MFA settings', 'error');
      }
    } catch (error) {
      console.error('Error updating MFA settings:', error);
      notify('Error updating MFA settings', 'error');
    }
  };

  return (
    <div className="pt-24 pb-16 w-full mx-auto dark:bg-black bg-white flex flex-col items-center">
      <div className="p-6 max-w-2xl shadow-md rounded-lg">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl dark:text-white font-bold text-gray-800">
            Multi-Factor Authentication Settings
          </h2>
          <div className="text-sm">
            <span className={`px-2 py-1 rounded ${mfaSettings.mfa_enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
              {mfaSettings.mfa_enabled ? `Enabled (${mfaSettings.mfa_method.join(', ')})` : 'Disabled'}
            </span>
          </div>
        </div>
        <p className="dark:text-zinc-400 text-gray-600 mb-6">
          Enhance your account security by enabling multi-factor authentication (MFA).
          You can enable multiple authentication methods for increased security.
        </p>
        <div className="space-y-6">
          {/* Email Authentication */}
          <div className="flex items-center justify-between border-b border-gray-300 pb-4">
            <div>
              <span className="text-lg font-medium dark:text-zinc-300 text-gray-700 block">
                Email Authentication
              </span>
              <span className="text-sm text-gray-500">
                {mfaSettings.mfa_method.includes('email') ? 'Currently active' : 'Not active'}
              </span>
            </div>
            <div className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={mfaSettings.mfa_method.includes('email')}
                onChange={() => handleToggleChange('email')}
                id="email-toggle"
                className="sr-only"
              />
              <label
                htmlFor="email-toggle"
                className={`flex items-center cursor-pointer`}
              >
                <div
                  className={`w-12 h-6 rounded-full shadow-inner transition-colors ${
                    mfaSettings.mfa_method.includes('email') ? "bg-blue-500" : "bg-gray-300"
                  }`}
                ></div>
                <div
                  className={`absolute left-1 top-1 w-4 h-4 bg-white rounded-full shadow transform transition-transform ${
                    mfaSettings.mfa_method.includes('email') ? "translate-x-6" : "translate-x-0"
                  }`}
                ></div>
              </label>
              <span className="ml-4 text-sm dark:text-zinc-200 text-gray-600">
                {mfaSettings.mfa_method.includes('email') ? "On" : "Off"}
              </span>
            </div>
          </div>

          {/* TOTP Authentication */}
          <div className="flex items-center justify-between border-b border-gray-300 pb-4">
            <div>
              <span className="text-lg font-medium dark:text-zinc-300 text-gray-700 block">
                Time-based OTP (TOTP)
              </span>
              <span className="text-sm text-gray-500">
                {mfaSettings.mfa_method.includes('totp') ? 'Currently active' : 'Not active'}
              </span>
            </div>
            <div className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={mfaSettings.mfa_method.includes('totp')}
                onChange={() => handleToggleChange('totp')}
                id="totp-toggle"
                className="sr-only"
              />
              <label
                htmlFor="totp-toggle"
                className={`flex items-center cursor-pointer`}
              >
                <div
                  className={`w-12 h-6 rounded-full shadow-inner transition-colors ${
                    mfaSettings.mfa_method.includes('totp') ? "bg-blue-500" : "bg-gray-300"
                  }`}
                ></div>
                <div
                  className={`absolute left-1 top-1 w-4 h-4 bg-white rounded-full shadow transform transition-transform ${
                    mfaSettings.mfa_method.includes('totp') ? "translate-x-6" : "translate-x-0"
                  }`}
                ></div>
              </label>
              <span className="ml-4 text-sm dark:text-zinc-200 text-gray-600">
                {mfaSettings.mfa_method.includes('totp') ? "On" : "Off"}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MfaSettings;
